import type { FC } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNursingHomeContext } from '@pflegenavi/frontend/nursing-home-context';
import { useGetPayoutsPhoenix } from '@pflegenavi/frontend/api-nursing-home';
import { Container } from '@mui/material';
import type { UrlSort } from '@pflegenavi/web-components';
import {
  urlString,
  useUrlFilters,
  useUrlTableState,
} from '@pflegenavi/web-components';
import { NursingHomeAccountingBreadcrumbs } from './NursingHomeAccountingBreadcrumbs';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { AccountingPayoutsTableMangopay } from './payoutsTable/AccountingPayoutsTableMangopay';
import { PayoutTabValues } from './hooks/useAccountingPayoutsPage';

export type PayoutSortColumn = 'transferred';

const SORT_COLUMNS: readonly PayoutSortColumn[] = ['transferred'];
const DEFAULT_SORT: UrlSort<PayoutSortColumn> = {
  column: 'transferred',
  direction: 'desc',
};
const FILTER_CODECS = { state: urlString('state') };

const MANGOPAY_STATE: Record<
  PayoutTabValues,
  'CREATED' | 'SUCCEEDED' | 'FAILED' | undefined
> = {
  [PayoutTabValues.All]: undefined,
  [PayoutTabValues.Paid]: 'SUCCEEDED',
  [PayoutTabValues.Pending]: 'CREATED',
  [PayoutTabValues.Failed]: 'FAILED',
};

export const AccountingPayoutsPageMangopay: FC = () => {
  const { t } = useTranslation();
  const { selectedNursingHome } = useNursingHomeContext();

  const { page, pageSize, sort, setPage, setPageSize, setSort } =
    useUrlTableState({
      defaultPageSize: 5,
      sortColumns: SORT_COLUMNS,
      defaultSort: DEFAULT_SORT,
    });
  const { values, set } = useUrlFilters(FILTER_CODECS);
  const tab =
    Object.values(PayoutTabValues).find((value) => value === values.state) ??
    PayoutTabValues.All;

  const { data: payoutsResult, isLoading } = useGetPayoutsPhoenix(
    {
      params: {
        nursing_home_id: selectedNursingHome?.id ?? '',
        page,
        pageSize,
        state: MANGOPAY_STATE[tab],
        orderBy: sort?.column,
        orderDirection: sort?.direction,
      },
    },
    {
      enabled: !!selectedNursingHome?.id,
    }
  );

  return (
    <Container maxWidth={'lg'}>
      <NursingHomeAccountingBreadcrumbs
        pageTitle={t('accounting.nav.payouts')}
        route={nhAppMainPages.NURSING_HOME_ACCOUNTING_PAYOUTS}
      />
      <AccountingPayoutsTableMangopay
        isLoading={isLoading}
        payouts={payoutsResult?.data}
        nursingHomeId={selectedNursingHome?.id ?? ''}
        totalCount={payoutsResult?.meta?.total_count ?? 0}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        tab={tab}
        onTabChange={(value) =>
          set('state', value === PayoutTabValues.All ? undefined : value)
        }
        sort={sort ?? DEFAULT_SORT}
        onSortChange={setSort}
      />
    </Container>
  );
};
